import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@host/stores';
import { Navigate, useLocation } from 'react-router-dom';
import { LoggedOutSupportShell } from '@host/layout/LoggedOutSupportShell';

export const LoggedOutGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getToken } = useAuthStore.getState();
  const [isUserGuest, setIsUserGuest] = useState(!!localStorage.getItem('isUserGuest'));

  const location = useLocation();
  const orgSlug = localStorage.getItem('org-slug');
  const workplaceRedirect = orgSlug ? `/${orgSlug}/workplace` : '/welcome';

  // NGE-681: the auditorium guest entry (/:orgSlug/welcome/:sessionPid/join) must
  // always show the guest form to start a fresh auditorium session — don't bounce
  // it to workplace/guest-workplace based on a leftover token or guest flag.
  const isAuditoriumGuestEntry = /\/welcome\/[^/]+\/join\/?$/.test(location.pathname);

  const [checking, setChecking] = useState(true);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    const verify = async () => {
      const token = await getToken();
      // Only check if token exists (don't check expiration)
      // Token refresh is handled automatically by respective systems
      setHasToken(!!token);

      setChecking(false);
    };
    verify();
  }, [getToken, orgSlug]);

  if (checking) return <div>Loading...</div>;

  if (isAuditoriumGuestEntry) {
    return <LoggedOutSupportShell>{children}</LoggedOutSupportShell>;
  }

  if (isUserGuest) {
    // Verify guest has an active session (sessionStorage clears when tab/browser closes)
    const redirectTo = orgSlug ? `/${orgSlug}/guest-workplace` : '/guest-workplace';
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (hasToken) {
    return <Navigate to={workplaceRedirect} state={{ from: location }} replace />;
  }

  return <LoggedOutSupportShell>{children}</LoggedOutSupportShell>;
};
